import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import classNames from 'classnames'
import { StatementIcon } from '@okcredit/web-lib'
import { THEME } from 'components/theme'
import { pushEvent } from 'state/new-summary/events/events.actions'
import { NEWDASHBOARD_EVENTS } from 'state/new-summary/events/events.types'
import AccountReport from 'pages/AccountReport'
import DeliveryTruckIcon from 'assets/new-icons/icon-delivery-truck.webp'
import { ENTITY_TYPE } from 'shared/constants'
import { TabsContainerStyles } from './styles'
import { ReportTabStyles } from './ReportTab.styles'

// Filled person — matches the customer glyph used elsewhere in the app.
const CustomerGlyph = (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <circle cx="12" cy="8" r="3.7" />
    <path d="M5 18.6c0-3.2 3.1-5.2 7-5.2s7 2 7 5.2a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z" />
  </svg>
)

// Delivery truck for suppliers.
const SupplierGlyph = <img src={DeliveryTruckIcon} alt="" />

// A rail item that, instead of navigating, opens a small menu (flyout to the right)
// letting the user choose a consolidated Customer or Supplier report. The chosen
// report reuses the existing AccountReport screen.
const ReportTab = () => {
  const tabClasses = TabsContainerStyles()
  const classes = ReportTabStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const itemRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })
  const [reportEntity, setReportEntity] = useState<ENTITY_TYPE | null>(null)

  const openMenu = () => {
    const rect = itemRef.current?.getBoundingClientRect()
    if (rect) {
      setMenuPos({ top: rect.top, left: rect.right + 10 })
    }
    dispatch(pushEvent(NEWDASHBOARD_EVENTS.REPORT_TAB))
    setOpen(true)
  }

  const toggleMenu = () => (open ? setOpen(false) : openMenu())

  const chooseEntity = (entity: ENTITY_TYPE) => {
    dispatch(
      pushEvent(
        entity === ENTITY_TYPE.CUSTOMER
          ? NEWDASHBOARD_EVENTS.REPORT_CUSTOMER_CLICKED
          : NEWDASHBOARD_EVENTS.REPORT_SUPPLIER_CLICKED
      )
    )
    setReportEntity(entity)
    setOpen(false)
  }

  return (
    <>
      <div
        ref={itemRef}
        className={tabClasses.iconTitleContainer}
        onClick={toggleMenu}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggleMenu()
          }
        }}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div
          className={classNames(tabClasses.iconContainer, {
            [tabClasses.activeIconContainer]: open
          })}
        >
          <StatementIcon fill={open ? THEME.brandGreen : THEME.brandBlack} size={24} />
        </div>
        <span className={classNames(tabClasses.title, { [tabClasses.activeText]: open })}>
          {t('dashboard:Report')}
        </span>
      </div>

      {open &&
        createPortal(
          <>
            <div className={classes.backdrop} onClick={() => setOpen(false)} />
            <div
              className={classes.flyout}
              style={{ top: menuPos.top, left: menuPos.left }}
              role="menu"
              aria-label={t('dashboard:View report')}
            >
              <div className={classes.flyoutTitle}>{t('dashboard:View report')}</div>
              <button
                type="button"
                className={classes.opt}
                role="menuitem"
                onClick={() => chooseEntity(ENTITY_TYPE.CUSTOMER)}
              >
                <span className={classNames(classes.optDot, classes.custDot)}>{CustomerGlyph}</span>
                {t('dashboard:Customers')}
              </button>
              <button
                type="button"
                className={classes.opt}
                role="menuitem"
                onClick={() => chooseEntity(ENTITY_TYPE.SUPPLIER)}
              >
                <span className={classNames(classes.optDot, classes.suppDot)}>{SupplierGlyph}</span>
                {t('dashboard:Supplier')}
              </button>
            </div>
          </>,
          document.body
        )}

      {reportEntity &&
        createPortal(
          <div className={classes.reportOverlay}>
            <AccountReport entityType={reportEntity} onClose={() => setReportEntity(null)} />
          </div>,
          document.body
        )}
    </>
  )
}

export default ReportTab
